import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import { inputStyles } from 'assets/components/inputs/Input';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import ErrorWrapper from 'assets/components/inputs/wrappers/ErrorWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import ThemeTableSimple2 from 'assets/components/table/themes/Theme.Table.Simple.2';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import {
  defaultDateTimeFormat,
  defaultInputDateFormat,
  defaultServerDateFormat,
  localizeDate,
} from 'assets/utils/data/Date';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import ContractRefundStatusCode from 'models/enums/ContractRefundStatusCode';
import RefundInfoTable from '../../../components/RefundInfoTable';
import {
  ContractRefundForm,
  useContractRefundProvider,
  useContractRefundRecalculation,
} from '../../../Sales.ContractRefund.Hooks';
import { formatPercentage } from 'assets/utils/data/Number';

export const refundInfoGridStyles: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: Theme.Size.L,
  fontSize: Theme.Size.M,
};

const SalesContractRefundProcessInfo = () => {
  const { lang } = getLocales();
  const { contract, isRecalculating } = useContractRefundProvider();
  const { form } = useModalForm<ContractRefundForm>();

  useContractRefundRecalculation(form);

  const getKmOrDateBasedLabel = (data: Partial<ContractRefundForm>) => {
    const hasMinimum = data?.refundConfigTemplateJson?.minimumRetainedPremium;
    const km = data?.remainingOdometerReadingPercentage;
    const days = data?.remainingTermInDaysPercentage;

    if (hasMinimum) {
      if (km && days && km < days) {
        return lang.km;
      }
    } else {
      if (km && data && km === data?.refundFactor) {
        return lang.km;
      }
    }
    return lang.dateBased;
  };
  return (
    <FormWrapper>
      <p
        style={{
          backgroundColor: Theme.Color.warning,
          color: 'white',
          padding: `${Theme.Size.M} ${Theme.Size.L}`,
          fontWeight: 'bold',
          marginTop: 0,
        }}
      >
        {lang.refundProcessWarning}
      </p>
      <InputWrapper>
        <DateInput
          name="refundDateAtLocal"
          label={lang.refundDate}
          dateTimeFormat={defaultInputDateFormat}
          isLocal={true}
          value={form?.data?.refundDateAtLocal}
          error={form?.errors?.refundDateAtLocal}
          onChange={form.update}
          min={dayjs().startOf('day').format(defaultServerDateFormat)}
          max={dayjs().endOf('day').format(defaultServerDateFormat)}
          icon={<Icon class="fas-calendar-alt" />}
        />
        <DateInput
          name="cancellationDateLocal"
          label={lang.cancellationDate}
          dateTimeFormat={defaultInputDateFormat}
          isLocal={true}
          value={form?.data?.contractCancellation?.cancellationDateLocal}
          error={form?.errors?.contractCancellation?.cancellationDateLocal}
          onChange={({ cancellationDateLocal }) =>
            form.merge({
              contractCancellation: {
                cancellationDateLocal,
              },
            })
          }
          icon={<Icon class="fas-calendar-alt" />}
        />
        <NumberInput
          name="odometerReading"
          label={lang.odometerReading}
          value={form?.data?.contractCancellation?.odometerReading}
          error={form?.errors?.contractCancellation?.odometerReading}
          onChange={({ odometerReading }) =>
            form.merge({
              contractCancellation: {
                odometerReading,
              },
            })
          }
        />
      </InputWrapper>
      <CheckWrapper style={{ display: 'flex', gap: Theme.Size.XL }}>
        <CheckInput
          name="isLoyaltyPremiumApplied"
          label={lang.loyaltyPremiumApplies}
          class={inputStyles.check.switch}
          value={form.data.isLoyaltyPremiumApplied}
          onChange={form.update}
          htmlElementProps={{ title: form.data.isLoyaltyPremiumApplied ? lang.disable : lang.enable }}
        />
        <CheckInput
          name="areClaimDeductionsApplied"
          label={lang.claimDeductionsApply}
          class={inputStyles.check.switch}
          value={form.data.areClaimDeductionsApplied}
          onChange={form.update}
          htmlElementProps={{ title: form.data.areClaimDeductionsApplied ? lang.disable : lang.enable }}
        />
      </CheckWrapper>
      &nbsp;
      {form.data?.isLoyaltyPremiumApplied && (
        <TextInput
          name="newAppNumber"
          label={lang.replacementContractNumber}
          value={form?.data?.newAppNumber}
          error={form?.errors?.newAppNumber}
          onChange={form.update}
        />
      )}
      <hr />
      {isRecalculating ? (
        <ErrorWrapper children={`${lang.recalculating}...`} style={{ width: '100%' }} />
      ) : (
        <>
          <h2>{lang.refundInformation}</h2>
          <table className={ThemeTableSimple2}>
            <thead>
              <tr>
                <th>{lang.refundInformation}</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ fontWeight: 'unset' }}>
                  {lang.jobId}: {form.data?.jobId}
                </td>
                <td>
                  {lang.status}: {form.data?.statusCode}
                </td>
                <td>
                  {lang.appNumber}: {contract?.appNumber}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 'unset' }}>
                  {lang.dateInitiated}: {localizeDate(form.data?.createdAtUtc, defaultDateTimeFormat)}
                </td>
                <td>
                  {lang.refundDate}: {localizeDate(form.data?.refundDateAtLocal, defaultDateTimeFormat)}
                </td>
                <td>
                  {lang.templateName}: {form.data?.refundConfigTemplateJson?.internalName}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 'unset' }}>
                  {lang.kmOrDateBased}: {getKmOrDateBasedLabel(form.data)}
                </td>
                <td>
                  {lang.minimumRetainedPremium}:{' '}
                  {formatPercentage(form.data?.refundConfigTemplateJson?.minimumRetainedPremium)}
                </td>
                <td>
                  {lang.refundFactorAfterMinimumRetained}: {0}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 'unset' }}>
                  {lang.refundQuoteAmount}: {<FormatValue value={`$${form.data?.refundedDealerTotal}`} />}
                </td>
                <td>
                  {lang.actualRefund}:{' '}
                  {form.data?.statusCode !== ContractRefundStatusCode.APPROVED ? (
                    'N/A'
                  ) : (
                    <FormatValue value={`$${form.data?.refundedDealerTotal}`} />
                  )}
                </td>
                <td>
                  {lang.hasClaims}: {!isEmpty(contract?.claims) || contract?.claimCount > 0 ? lang.yes : lang.no}
                </td>
              </tr>
            </tbody>
          </table>
          &nbsp;
          <RefundInfoTable
            refundPriceInfo={
              {
                ...form.data,
                refundConfigTemplate: form.data?.refundConfigTemplateJson ?? form.data?.refundConfigTemplate,
              } as Api.Sales.ContractRefund.RefundPricingInfo.RefundPriceInfo
            }
            contract={contract}
            contractCancellation={form.data.contractCancellation as Model.IContractCancellation}
          />
        </>
      )}
    </FormWrapper>
  );
};

export default SalesContractRefundProcessInfo;
